// KPICard Component - Premium stat card for dashboard
import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../lib/utils';

export const KPICard = ({ 
  title, 
  value, 
  icon: Icon, 
  change, 
  changeLabel,
  prefix = '',
  suffix = '',
  color = 'primary',
  delay = 0 
}) => {
  const colorStyles = {
    primary: 'from-primary/10 to-primary/5 border-primary/20',
    success: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
    danger: 'from-red-500/10 to-red-500/5 border-red-500/20',
    warning: 'from-amber-500/10 to-amber-500/5 border-amber-500/20',
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-500/20',
  };

  const iconColors = {
    primary: 'text-primary bg-primary/10',
    success: 'text-emerald-600 bg-emerald-500/10',
    danger: 'text-red-600 bg-red-500/10',
    warning: 'text-amber-600 bg-amber-500/10',
    blue: 'text-blue-600 bg-blue-500/10',
  };

  const isPositive = change > 0;
  const isNegative = change < 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      className={cn(
        "relative bg-white dark:bg-slate-900 rounded-xl border shadow-card overflow-hidden",
        "hover:shadow-card-hover transition-shadow duration-300"
      )}
    >
      {/* Gradient Background */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br opacity-50",
        colorStyles[color]
      )} />
      
      <div className="relative p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">
              {prefix}{typeof value === 'number' ? value.toLocaleString('fr-FR') : value}{suffix}
            </p>
            
            {change !== undefined && (
              <div className="flex items-center gap-1.5">
                {isPositive && (
                  <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                    <TrendingUp className="w-3 h-3" />
                    +{change}%
                  </span>
                )}
                {isNegative && (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-500/10 px-2 py-0.5 rounded-full">
                    <TrendingDown className="w-3 h-3" />
                    {change}%
                  </span>
                )}
                {!isPositive && !isNegative && change === 0 && (
                  <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    <Minus className="w-3 h-3" />
                    0%
                  </span>
                )}
                {changeLabel && (
                  <span className="text-xs text-muted-foreground">{changeLabel}</span>
                )}
              </div>
            )}
          </div>
          
          {Icon && (
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              iconColors[color]
            )}>
              <Icon className="w-6 h-6" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default KPICard;
